import React from "react";
import { EXPERTISE } from "@/constants/testIds";
import { HeartPulse, ShieldCheck, PiggyBank, Building2, ArrowUpRight } from "lucide-react";

const IRD_IMG =
  "https://images.unsplash.com/photo-1488972685288-c3fd157d7c7a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBhYnN0cmFjdCUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3ODMxNzE3ODV8MA&ixlib=rb-4.1.0&q=85";

const items = [
  {
    id: EXPERTISE.cardVie,
    tag: "Assurance vie",
    icon: HeartPulse,
    title: "Contrats d'assurance vie",
    body: "Architecture patrimoniale sur-mesure, gestion pilotée ou libre, unités de compte sélectionnées avec exigence.",
    span: "md:col-span-7",
    tone: "light",
  },
  {
    id: EXPERTISE.cardPer,
    tag: "Retraite",
    icon: PiggyBank,
    title: "Plan Épargne Retraite (PER)",
    body: "PER individuel et collectif. Fiscalité optimisée à l'entrée, sortie souple en capital ou rente.",
    span: "md:col-span-5",
    tone: "dark",
  },
  {
    id: EXPERTISE.cardPrev,
    tag: "Prévoyance",
    icon: ShieldCheck,
    title: "Prévoyance & Santé",
    body: "Couverture décès, invalidité, incapacité, dépendance. Complémentaires santé haut de gamme individuelles et collectives.",
    span: "md:col-span-5",
    tone: "cream",
  },
  {
    id: EXPERTISE.cardIrd,
    tag: "IRD",
    icon: Building2,
    title: "Incendie, Risques Divers",
    body: "Multirisque professionnelle, RC pro, flotte, dommages aux biens, cyber, homme-clé, perte d'exploitation.",
    span: "md:col-span-7",
    tone: "image",
    image: IRD_IMG,
  },
];

function Card({ item }) {
  const Icon = item.icon;
  const base =
    "group relative overflow-hidden p-8 md:p-10 min-h-[280px] border transition-all duration-500";

  if (item.tone === "dark") {
    return (
      <article
        data-testid={item.id}
        className={`${base} ${item.span} bg-[#0a192f] border-[#0a192f] text-[#efeade] hover:bg-[#0a192f]/95`}
      >
        <div className="flex items-start justify-between">
          <span className="overline text-[#b08d57]">{item.tag}</span>
          <Icon size={22} strokeWidth={1.25} className="text-[#b08d57]" />
        </div>
        <h3 className="mt-10 font-serif text-3xl md:text-4xl leading-tight">
          {item.title}
        </h3>
        <p className="mt-4 text-[#efeade]/70 max-w-md leading-relaxed">
          {item.body}
        </p>
        <ArrowUpRight
          size={20}
          strokeWidth={1.25}
          className="absolute bottom-8 right-8 text-[#c05c3d] transition-transform duration-500 group-hover:-translate-y-1 group-hover:translate-x-1"
        />
      </article>
    );
  }

  if (item.tone === "cream") {
    return (
      <article
        data-testid={item.id}
        className={`${base} ${item.span} bg-[#efeade] border-[#0a192f]/10 text-[#0a192f] hover:-translate-y-1`}
      >
        <div className="flex items-start justify-between">
          <span className="overline">{item.tag}</span>
          <Icon size={22} strokeWidth={1.25} className="text-[#c05c3d]" />
        </div>
        <h3 className="mt-10 font-serif text-3xl md:text-4xl leading-tight">
          {item.title}
        </h3>
        <p className="mt-4 text-[#475569] max-w-md leading-relaxed">
          {item.body}
        </p>
        <ArrowUpRight
          size={20}
          strokeWidth={1.25}
          className="absolute bottom-8 right-8 text-[#0a192f] transition-transform duration-500 group-hover:-translate-y-1 group-hover:translate-x-1"
        />
      </article>
    );
  }

  if (item.tone === "image") {
    return (
      <article
        data-testid={item.id}
        className={`${base} ${item.span} border-[#0a192f]/10 text-[#efeade] relative isolate hover:-translate-y-1`}
      >
        <div
          className="absolute inset-0 bg-cover bg-center -z-10"
          style={{ backgroundImage: `url(${item.image})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#0a192f]/95 via-[#0a192f]/70 to-[#0a192f]/30 -z-10" />
        <div className="flex items-start justify-between">
          <span className="overline text-[#b08d57]">{item.tag}</span>
          <Icon size={22} strokeWidth={1.25} className="text-[#b08d57]" />
        </div>
        <h3 className="mt-10 font-serif text-3xl md:text-4xl leading-tight">
          {item.title}
        </h3>
        <p className="mt-4 text-[#efeade]/80 max-w-md leading-relaxed">
          {item.body}
        </p>
        <ArrowUpRight
          size={20}
          strokeWidth={1.25}
          className="absolute bottom-8 right-8 text-[#c05c3d] transition-transform duration-500 group-hover:-translate-y-1 group-hover:translate-x-1"
        />
      </article>
    );
  }

  return (
    <article
      data-testid={item.id}
      className={`${base} ${item.span} bg-white border-[#0a192f]/10 hover:-translate-y-1`}
    >
      <div className="flex items-start justify-between">
        <span className="overline">{item.tag}</span>
        <Icon size={22} strokeWidth={1.25} className="text-[#0a192f]" />
      </div>
      <h3 className="mt-10 font-serif text-3xl md:text-4xl leading-tight text-[#0a192f]">
        {item.title}
      </h3>
      <p className="mt-4 text-[#475569] max-w-md leading-relaxed">
        {item.body}
      </p>
      <ArrowUpRight
        size={20}
        strokeWidth={1.25}
        className="absolute bottom-8 right-8 text-[#c05c3d] transition-transform duration-500 group-hover:-translate-y-1 group-hover:translate-x-1"
      />
    </article>
  );
}

export default function Expertise() {
  return (
    <section
      id="expertise"
      data-testid={EXPERTISE.root}
      className="relative py-24 md:py-32 bg-[#f7f5f0]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-16 items-end">
          <div className="md:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-[#c05c3d]" />
              <span className="overline">Nos expertises</span>
            </div>
            <h2 className="font-serif text-4xl md:text-6xl leading-[1.05] text-[#0a192f]">
              Deux pôles.<br />
              <span className="italic text-[#c05c3d]">Un seul cabinet</span>{" "}
              — le vôtre.
            </h2>
          </div>
          <div className="md:col-span-5">
            <p className="text-[#475569] text-lg leading-relaxed">
              Notre indépendance nous permet de sélectionner, dans un marché
              exigeant, les solutions les plus pertinentes pour l&apos;entreprise et
              son dirigeant. Vie et IRD sous un même toit — pour une vision
              cohérente de vos risques.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {items.map((it) => (
            <Card key={it.id} item={it} />
          ))}
        </div>
      </div>
    </section>
  );
}
